/**
 * @module PriceTag
 *
 * Displays pricing information with currency symbol and frequency.
 * Automatically formats decimal values and includes accessible labeling.
 *
 * @example
 * // Basic price display
 * <PriceTag currencySymbol="$" price="9.99" frequency="month" />
 *
 * @example
 * // Annual pricing
 * <PriceTag currencySymbol="£" price="99.00" frequency="year" />
 */
import type * as React from "react";
import type { TextProps } from "./typography/text";
import { Text } from "./typography/text";

/**
 * Props for PriceTag component.
 * @property currencySymbol - Currency symbol (e.g., "$", "£", "€")
 * @property price - Price value as string (e.g., "9.99")
 * @property frequency - Billing frequency (e.g., "month", "year")
 */
export interface PriceTagProps extends TextProps {
  currencySymbol: string;
  frequency: string;
  price: string;
  ref?: React.Ref<HTMLParagraphElement>;
}
function PriceTag({
  currencySymbol,
  price,
  frequency,
  ref,
  ...props
}: PriceTagProps) {
  // Split price into main unit and cents/pence
  const [mainUnit, cents = "00"] = price.split(".", 2);

  // Create accessible label for screen readers
  const accessiblePrice = `${currencySymbol}${price} per ${frequency}`;
  return <Text className="font-sans font-normal leading-5" ref={ref} {...props} data-sentry-element="Text" data-sentry-component="PriceTag" data-sentry-source-file="price-tag.tsx">
      {/* Screen reader only text */}
      <span className="sr-only">{accessiblePrice}</span>

      {/* Visual presentation hidden from screen readers */}
      <span aria-hidden="true">
        <span className="text-gray-700 text-xs dark:text-gray-200">
          {currencySymbol}
        </span>
        <span className="font-sans font-semibold text-base text-gray-950 dark:text-white">
          {mainUnit}.{cents}
        </span>
        <span className="text-gray-700 text-xs dark:text-gray-200">
          /{frequency}
        </span>
      </span>
    </Text>;
}
export { PriceTag };